<template>
   <div data-cy="date-range-v1">
      <div class="mt-4 md:flex md:gap-12">
         <!-- Start date -->
         <div>
            <p aria-hidden="true">{{ $t("validation.date.start_date") }}</p>
            <DateInputs
               :id="`${block.id}-start`"
               :isFormatDDMMYY="state.requiredDateFormat === 'dd-mm-yyyy'"
               :isRequired="!!props.block.isRequired"
               :validations="props.block.data?.validations?.startDate || {}"
               :showBlockErrors="showBlockErrors"
               :existingAnswer="{ year: state.input.year, month: state.input.month, day: state.input.day }"
               :dayLabel="`${$t('validation.date.start_date')} ${$t('validation.date.day.period')}`"
               :monthLabel="`${$t('validation.date.start_date')} ${$t('validation.date.month.period')}`"
               :yearLabel="`${$t('validation.date.start_date')} ${$t('validation.date.year.period')}`"
               :ariaOnlyLabel="true"
               @inputs-update="processInputs"
            />
         </div>
         <!-- End Date -->
         <div>
            <p aria-hidden="true">{{ $t("validation.date.end_date") }}</p>
            <DateInputs
               :id="`${block.id}-end`"
               :isFormatDDMMYY="state.requiredDateFormat === 'dd-mm-yyyy'"
               :isRequired="!!props.block.isRequired"
               :validations="props.block.data?.validations?.endDate || {}"
               :showBlockErrors="showBlockErrors"
               :existingAnswer="{ year: state.endInput.year, month: state.endInput.month, day: state.endInput.day }"
               :dayLabel="`${$t('validation.date.end_date')} ${$t('validation.date.day.period')}`"
               :monthLabel="`${$t('validation.date.end_date')} ${$t('validation.date.month.period')}`"
               :yearLabel="`${$t('validation.date.end_date')} ${$t('validation.date.year.period')}`"
               :ariaOnlyLabel="true"
               @inputs-update="processInputs"
            />
         </div>
      </div>
      <!-- Range validation -->
      <InvalidInputsList
         v-if="showBlockErrors && (!state.rangeFullyComplete || !state.startIsBeforeEnd)"
         :errorList="errorList"
      />
   </div>
</template>

<script setup lang="ts">
import { useI18n } from "@/vue-i18n/vue-i18n";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import type { FlowselBlock } from "@/components/flowsel/flowsel.type";
import type { AnswerResp } from "@/data/api-layer/answersApi";
import type { dateInputsEmits } from "@/components/flowsel/_inputs/DateInputs.vue";
import { getDateInputFromFormatted, getFormattedFromDateInput } from "./date-utils";

export interface DateRangeAnswerData {
   start_date: string;
   end_date: string;
}

export interface DateRangeBlock extends FlowselBlock {
   data: {
      displayFormat: "dd-mm-yyyy" | "yyyy-mm-dd";
      validations: {
         startDate: {
            min?: string;
            max?: string;
         };
         endDate: {
            min?: string;
            max?: string;
         };
      };
   };
}

interface Props {
   block: DateRangeBlock;
   userBlockAnswer: AnswerResp | null;
   showBlockErrors: boolean;
}

// declarations
// -----------------------------------------
const DATE_FORMAT = "YYYY-MM-DD";
dayjs.extend(customParseFormat);

const emit = defineEmits(["update-valid", "update-answer"]);
const { t: $t } = useI18n();
const props = defineProps<Props>();

const state = reactive({
   input: {
      day: "",
      month: "",
      year: "",
   },
   endInput: {
      day: "",
      month: "",
      year: "",
   },
   requiredDateFormat: unref(props.block.data.displayFormat),
   startIsBeforeEnd: false,
   rangeFullyComplete: false,
});

// Computed
// -----------------------------------------
const errorList = computed(() => {
   if (!state.startIsBeforeEnd) {
      return [
         {
            inputId: props.block?.id,
            validator: "endBeforeStart",
            message: $t("validation.date.range.end_before_start"),
         },
      ];
   } else if (!state.rangeFullyComplete) {
      return [
         {
            inputId: props.block?.id,
            validator: "rangeIncomplete",
            message: $t("validation.date.range.range_required"),
         },
      ];
   }

   return [];
});

// Methods
// -----------------------------------------
// update the inputs with any existing answer data
const initialiseComponent = function () {
   const baseDate = props.userBlockAnswer?.data?.startDate;
   const endDate = props.userBlockAnswer?.data?.endDate;

   if (baseDate) {
      state.input = getDateInputFromFormatted(baseDate, DATE_FORMAT);
   }
   if (endDate) {
      state.endInput = getDateInputFromFormatted(endDate, DATE_FORMAT);
   }
};

const checkRangeValidations = () => {
   state.startIsBeforeEnd = false;
   state.rangeFullyComplete = false;
   const start = new Date(parseInt(state.input.year), parseInt(state.input.month) - 1, parseInt(state.input.day));
   const end = new Date(
      parseInt(state.endInput.year),
      parseInt(state.endInput.month) - 1,
      parseInt(state.endInput.day)
   );

   if (
      !state.input.day &&
      !state.input.month &&
      !state.input.year &&
      !state.endInput.day &&
      !state.endInput.month &&
      !state.endInput.year
   ) {
      // if all fields are empty, the range is valid (to allow for optional questions)
      state.startIsBeforeEnd = true;
      state.rangeFullyComplete = true;
   } else if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      // Show error if one date of a range is valid but the other is missing
      state.startIsBeforeEnd = true;
      state.rangeFullyComplete = false;
   } else if (end.getTime() < start.getTime()) {
      // Or if the start date is not before the end
      state.startIsBeforeEnd = false;
      state.rangeFullyComplete = true;
   } else {
      // Otherwise the range is valid
      state.startIsBeforeEnd = true;
      state.rangeFullyComplete = true;
   }
};

function processInputs(inputsInfo: dateInputsEmits) {
   const newInput = {
      year: inputsInfo.year,
      month: inputsInfo.month,
      day: inputsInfo.day,
   };
   if (inputsInfo.id.includes("end")) {
      state.endInput = newInput;
   } else {
      state.input = newInput;
   }
   // Here we check range-specific validation which isn't covered by the DateInputs components
   checkRangeValidations();

   if (!inputsInfo.valid || !state.rangeFullyComplete || !state.startIsBeforeEnd) {
      // an input is invalid (some missing required fields / has errors)
      emit("update-valid", false);
   } else {
      // all inputs are valid. This may be because there is a valid answer, or no answer when this question is not required.
      // For no answer, an empty data format is required by the BE.
      let answerToSubmit = null;
      if (inputsInfo.year && inputsInfo.month && inputsInfo.day) {
         answerToSubmit = {
            start_date: getFormattedFromDateInput(state.input, DATE_FORMAT),
            end_date: getFormattedFromDateInput(state.endInput, DATE_FORMAT),
         };
      }
      emit("update-valid", true);
      emit("update-answer", answerToSubmit);
   }
}

// On load, check for an existing answer or for user data
initialiseComponent();
</script>
